/**
 * StaffScheduleSettings — weekly rota view for staff shifts.
 *
 * The page shows a facility-scoped week picker. Each day column lists the
 * shifts scheduled for that day. Shifts are stored in UTC; display conversion
 * uses the browser's Intl API with the facility's stored timezone (passed
 * through /me facilitiesInScope). This keeps the component locale-agnostic.
 *
 * Capability gate: reads are open to any authenticated staff. Create / edit /
 * delete require staff.edit_roles (owners + managers by default).
 */
import { useAuthContext } from '@/auth/useAuthContext';
import { Modal } from '@/components/Modal';
import {
  ApiError,
  createStaffShift,
  deleteStaffShift,
  getTeam,
  listStaffShifts,
  updateStaffShift,
} from '@/lib/api';
import type {
  Facility,
  ShiftStatus,
  StaffShift,
  TeamMember,
  UpdateStaffShiftInput,
} from '@/lib/types';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock,
  Edit2,
  Plus,
  Trash2,
  User,
  Wind,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Date utilities (all UTC-safe; display uses Intl)
// ---------------------------------------------------------------------------

/** Returns the Monday of the week containing the given date (UTC). */
function getWeekStart(d: Date): Date {
  const day = d.getUTCDay(); // 0=Sun … 6=Sat
  const diff = day === 0 ? -6 : 1 - day; // shift so Mon=0
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() + diff);
  monday.setUTCHours(0, 0, 0, 0);
  return monday;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setUTCDate(r.getUTCDate() + n);
  return r;
}

/** Short ISO date string YYYY-MM-DD from a UTC Date. */
function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Full UTC ISO 8601 string for start-of-day in UTC. */
function toStartOfDay(d: Date): string {
  const c = new Date(d);
  c.setUTCHours(0, 0, 0, 0);
  return c.toISOString();
}

/** Full UTC ISO 8601 string for end-of-day (23:59:59) in UTC. */
function toEndOfDay(d: Date): string {
  const c = new Date(d);
  c.setUTCHours(23, 59, 59, 999);
  return c.toISOString();
}

/** Format a UTC ISO string as a local time in the given IANA timezone. */
function fmtTime(isoUtc: string, tz: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(isoUtc));
}

/** Format a UTC ISO string as a date string in a timezone. */
function fmtDate(isoUtc: string, tz: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(new Date(isoUtc));
}

/** Render an instant as wall-clock Date.UTC millis in the given IANA timezone. */
function wallClockUtcMs(instantMs: number, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(instantMs));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  // Some ICU builds render midnight as "24" under hour12:false.
  const hour = get('hour') % 24;
  return Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'));
}

/**
 * Convert local date YYYY-MM-DD + local time HH:MM in an IANA timezone to a
 * UTC ISO string.
 *
 * Approach: treat the desired wall-clock as if it were UTC to get a first
 * guess, then format that guess back into the target timezone (year / month /
 * day / hour / minute — so day rollover across the date line or a negative
 * offset is accounted for) and shift the guess by the difference. A second
 * pass corrects the rare case where the first shift crosses a DST boundary.
 * For nonexistent spring-forward times this converges to the shifted instant.
 */
function localDateTimeToUtc(date: string, time: string, tz: string): string {
  const [y = 0, mo = 1, d = 1] = date.split('-').map(Number);
  const [h = 0, m = 0] = time.split(':').map(Number);
  const desired = Date.UTC(y, mo - 1, d, h, m);
  let ts = desired;
  for (let i = 0; i < 2; i++) {
    const rendered = wallClockUtcMs(ts, tz);
    if (rendered === desired) break;
    ts += desired - rendered;
  }
  return new Date(ts).toISOString();
}

// Exported for unit tests only.
export { localDateTimeToUtc as __localDateTimeToUtcForTests };

const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ---------------------------------------------------------------------------
// Status pill
// ---------------------------------------------------------------------------

function ShiftStatusPill({ status }: { status: ShiftStatus }) {
  const cfg: Record<ShiftStatus, { bg: string; text: string }> = {
    scheduled: { bg: 'bg-secondary', text: 'text-muted-foreground' },
    published: { bg: 'bg-success/10', text: 'text-success' },
    cancelled: { bg: 'bg-destructive/10', text: 'text-destructive' },
  };
  const { bg, text } = cfg[status] ?? cfg.scheduled;
  return (
    <span
      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium capitalize ring-hairline ${bg} ${text}`}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Shift editor modal
// ---------------------------------------------------------------------------

interface ShiftFormState {
  membershipId: string;
  date: string; // YYYY-MM-DD (local to facility tz)
  startTime: string; // HH:MM
  endTime: string; // HH:MM
  roleLabel: string;
  status: ShiftStatus;
  notes: string;
}

function defaultShiftForm(_facilityId: string, date?: string): ShiftFormState {
  return {
    membershipId: '',
    date: date ?? toDateStr(new Date()),
    startTime: '08:00',
    endTime: '16:00',
    roleLabel: '',
    status: 'scheduled',
    notes: '',
  };
}

interface ShiftEditorProps {
  shift: StaffShift | null;
  defaultDate?: string | undefined;
  facility: Facility;
  memberships: { id: string; label: string }[];
  onClose: () => void;
  onSaved: () => void;
}

function ShiftEditor({
  shift,
  defaultDate,
  facility,
  memberships,
  onClose,
  onSaved,
}: ShiftEditorProps) {
  const tz = facility.timezone ?? 'UTC';
  const isCreate = shift === null;

  const [form, setForm] = useState<ShiftFormState>(() => {
    if (!isCreate && shift) {
      return {
        membershipId: shift.membershipId,
        date: new Intl.DateTimeFormat('en-CA', {
          timeZone: tz,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(new Date(shift.startsAt)),
        startTime: fmtTime(shift.startsAt, tz),
        endTime: fmtTime(shift.endsAt, tz),
        roleLabel: shift.roleLabel ?? '',
        status: shift.status as ShiftStatus,
        notes: shift.notes ?? '',
      };
    }
    return defaultShiftForm(facility.id, defaultDate);
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!form.membershipId) {
      setError('Please select a staff member');
      return;
    }
    if (!form.date || !form.startTime || !form.endTime) {
      setError('Date and times are required');
      return;
    }
    const startsAt = localDateTimeToUtc(form.date, form.startTime, tz);
    const endsAt = localDateTimeToUtc(form.date, form.endTime, tz);
    if (endsAt <= startsAt) {
      setError('End time must be after start time');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (isCreate) {
        await createStaffShift({
          facilityId: facility.id,
          membershipId: form.membershipId,
          startsAt,
          endsAt,
          roleLabel: form.roleLabel.trim() || null,
          status: form.status,
          notes: form.notes.trim() || null,
        });
      } else {
        const patch: UpdateStaffShiftInput = {
          startsAt,
          endsAt,
          roleLabel: form.roleLabel.trim() || null,
          status: form.status,
          notes: form.notes.trim() || null,
        };
        await updateStaffShift(shift!.id, patch);
      }
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `Could not save the shift (${err.code}).`
          : 'Something went wrong',
      );
    } finally {
      setBusy(false);
    }
  }

  const label = (text: string) => (
    <label className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
      {text}
    </label>
  );
  const inputCls =
    'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" aria-label={isCreate ? 'Add shift' : 'Edit shift'}>
      <form
        className="flex flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
        <div className="flex items-center justify-between border-b border-border px-6 pt-5 pb-4">
          <h2 className="font-heading text-lg font-medium">
            {isCreate ? 'Add shift' : 'Edit shift'}
          </h2>
        </div>

        <div className="space-y-4 p-6">
          <div>
            {label('Staff member')}
            <select
              value={form.membershipId}
              onChange={(e) => setForm((p) => ({ ...p, membershipId: e.target.value }))}
              className={inputCls}
            >
              <option value="">Select staff member…</option>
              {memberships.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            {label('Date')}
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
              className={inputCls}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              {label('Start')}
              <input
                type="time"
                value={form.startTime}
                onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
                className={inputCls}
              />
            </div>
            <div>
              {label('End')}
              <input
                type="time"
                value={form.endTime}
                onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            {label('Role label (optional)')}
            <input
              type="text"
              value={form.roleLabel}
              onChange={(e) => setForm((p) => ({ ...p, roleLabel: e.target.value }))}
              placeholder="e.g. Lead groomer"
              maxLength={80}
              className={inputCls}
            />
          </div>

          <div>
            {label('Status')}
            <select
              value={form.status}
              onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as ShiftStatus }))}
              className={inputCls}
            >
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            {label('Notes (optional)')}
            <textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              placeholder="Internal notes for this shift"
              className={inputCls}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Saving…' : isCreate ? 'Add shift' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Delete confirmation modal
// ---------------------------------------------------------------------------

interface DeleteShiftModalProps {
  shift: StaffShift;
  tz: string;
  onCancel: () => void;
  onConfirmed: () => void;
}

function DeleteShiftModal({ shift, tz, onCancel, onConfirmed }: DeleteShiftModalProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setBusy(true);
    try {
      await deleteStaffShift(shift.id);
      onConfirmed();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `Could not remove the shift (${err.code}).`
          : 'Something went wrong',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onCancel} maxWidth="max-w-md" aria-label="Confirm delete shift">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Remove shift?</h2>
        <p className="text-sm text-muted-foreground">
          {fmtDate(shift.startsAt, tz)}, {fmtTime(shift.startsAt, tz)}–{fmtTime(shift.endsAt, tz)}
          {shift.roleLabel ? ` · ${shift.roleLabel}` : ''}
        </p>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleConfirm()}
            className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground press disabled:opacity-50"
          >
            {busy ? 'Removing…' : 'Remove shift'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Shift card (within a day column)
// ---------------------------------------------------------------------------

interface ShiftCardProps {
  shift: StaffShift;
  tz: string;
  memberLabel: (id: string) => string;
  onEdit: () => void;
  onDelete: () => void;
}

function ShiftCard({ shift, tz, memberLabel, onEdit, onDelete }: ShiftCardProps) {
  return (
    <div className="rounded-lg border border-border bg-surface p-2.5 text-sm space-y-1">
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 text-xs font-medium text-foreground truncate">
            <User className="size-3 shrink-0 text-muted-foreground" strokeWidth={1.75} />
            {memberLabel(shift.membershipId)}
          </p>
          <p className="flex items-center gap-1 text-xs text-muted-foreground num mt-0.5">
            <Clock className="size-3 shrink-0" strokeWidth={1.75} />
            {fmtTime(shift.startsAt, tz)}–{fmtTime(shift.endsAt, tz)}
          </p>
          {shift.roleLabel && (
            <p className="mt-0.5 text-[10px] text-muted-foreground truncate">{shift.roleLabel}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            title="Edit shift"
            className="p-0.5 text-muted-foreground hover:text-foreground press"
          >
            <Edit2 className="size-3" strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            title="Remove shift"
            className="p-0.5 text-muted-foreground hover:text-destructive press"
          >
            <Trash2 className="size-3" strokeWidth={1.75} />
          </button>
        </div>
      </div>
      <ShiftStatusPill status={shift.status as ShiftStatus} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Week grid
// ---------------------------------------------------------------------------

interface WeekGridProps {
  weekStart: Date; // UTC Monday
  shifts: StaffShift[];
  facility: Facility;
  memberLabel: (id: string) => string;
  onAddShift: (date: string) => void;
  onEditShift: (shift: StaffShift) => void;
  onDeleteShift: (shift: StaffShift) => void;
}

function WeekGrid({
  weekStart,
  shifts,
  facility,
  memberLabel,
  onAddShift,
  onEditShift,
  onDeleteShift,
}: WeekGridProps) {
  const tz = facility.timezone ?? 'UTC';

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  /** Group shifts by facility-local date YYYY-MM-DD. */
  const byDate = new Map<string, StaffShift[]>();
  for (const s of shifts) {
    const localDate = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(s.startsAt));
    const list = byDate.get(localDate) ?? [];
    list.push(s);
    byDate.set(localDate, list);
  }

  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[700px]" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {days.map((day, i) => {
          const dateStr = toDateStr(day); // UTC date, but Mon–Sun alignment
          const localDate = new Intl.DateTimeFormat('en-CA', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }).format(day);
          const dayShifts = (byDate.get(localDate) ?? []).sort((a, b) =>
            a.startsAt.localeCompare(b.startsAt),
          );
          const isToday = toDateStr(new Date()) === dateStr;

          return (
            <div
              key={dateStr}
              className={[
                'border-r border-border last:border-r-0',
                i === 5 || i === 6 ? 'bg-secondary/20' : '',
              ].join(' ')}
            >
              {/* Day header */}
              <div
                className={[
                  'border-b border-border px-2 py-2 text-center',
                  isToday ? 'bg-primary/5' : '',
                ].join(' ')}
              >
                <p
                  className={[
                    'text-[10.5px] font-semibold uppercase tracking-[0.12em]',
                    isToday ? 'text-primary' : 'text-muted-foreground',
                  ].join(' ')}
                >
                  {DOW_LABELS[i]}
                </p>
                <p
                  className={[
                    'num text-sm font-medium',
                    isToday ? 'text-primary' : 'text-foreground',
                  ].join(' ')}
                >
                  {new Intl.DateTimeFormat('en-GB', {
                    timeZone: tz,
                    day: 'numeric',
                    month: 'short',
                  }).format(day)}
                </p>
              </div>

              {/* Shifts list */}
              <div className="min-h-[120px] space-y-1.5 p-1.5">
                {dayShifts.map((s) => (
                  <ShiftCard
                    key={s.id}
                    shift={s}
                    tz={tz}
                    memberLabel={memberLabel}
                    onEdit={() => onEditShift(s)}
                    onDelete={() => onDeleteShift(s)}
                  />
                ))}
                {/* Add shift button */}
                <button
                  type="button"
                  onClick={() => onAddShift(localDate)}
                  className="flex w-full items-center justify-center gap-1 rounded-md py-1 text-[10.5px] text-muted-foreground hover:bg-secondary hover:text-foreground press transition-colors"
                  title={`Add shift on ${DOW_LABELS[i]}`}
                >
                  <Plus className="size-3" strokeWidth={2} />
                  Add
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal state
// ---------------------------------------------------------------------------

type ModalState =
  | { kind: 'none' }
  | { kind: 'editor'; shift: StaffShift | null; defaultDate?: string }
  | { kind: 'delete'; shift: StaffShift };

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function StaffScheduleSettings() {
  const { state } = useAuthContext();
  const me = state.me;
  const facilities = me?.facilitiesInScope ?? [];
  const defaultFacility = me?.activeFacility ?? facilities[0] ?? null;

  const [facility, setFacility] = useState<Facility | null>(defaultFacility);
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(new Date()));
  const [shifts, setShifts] = useState<StaffShift[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });

  // Use facility from context whenever it changes and no override is set.
  useEffect(() => {
    if (defaultFacility && !facility) setFacility(defaultFacility);
  }, [defaultFacility, facility]);

  // Load the team once so the member dropdown has name + email labels.
  useEffect(() => {
    getTeam()
      .then((members) => setTeamMembers(members.filter((m) => m.status === 'active')))
      .catch(() => {});
  }, []);

  const memberLabel = useCallback(
    (membershipId: string): string => {
      const m = teamMembers.find((x) => x.id === membershipId);
      if (!m) return membershipId.slice(0, 8);
      return m.userName ? `${m.userName} (${m.userEmail})` : m.userEmail;
    },
    [teamMembers],
  );

  const memberOptions = teamMembers.map((m) => ({
    id: m.id,
    label: m.userName ? `${m.userName} (${m.userEmail})` : m.userEmail,
  }));

  const refresh = useCallback(async () => {
    if (!facility) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setPermissionDenied(false);
    setLoadError(null);
    try {
      const from = toStartOfDay(weekStart);
      const to = toEndOfDay(addDays(weekStart, 6));
      const data = await listStaffShifts(facility.id, from, to);
      setShifts(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setPermissionDenied(true);
      } else {
        setLoadError(
          err instanceof ApiError
            ? `Could not load this week's shifts (${err.code}).`
            : "Could not load this week's shifts.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [facility, weekStart]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function prevWeek() {
    setWeekStart((w) => addDays(w, -7));
  }
  function nextWeek() {
    setWeekStart((w) => addDays(w, 7));
  }
  function goToday() {
    setWeekStart(getWeekStart(new Date()));
  }

  const weekLabel = (() => {
    const end = addDays(weekStart, 6);
    const fmt = (d: Date) =>
      new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(
        d,
      );
    return `${fmt(weekStart)} – ${fmt(end)}`;
  })();

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to view staff shifts.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Staff schedule</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Weekly rota — shifts are stored in UTC and displayed in the facility's local time.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: 'editor', shift: null })}
          className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press"
        >
          <Plus className="size-4" strokeWidth={1.75} />
          Add shift
        </button>
      </header>

      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Facility picker (multi-facility tenants) */}
        {facilities.length > 1 && (
          <div className="flex items-center gap-2">
            <label
              htmlFor="sched-facility"
              className="text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Facility
            </label>
            <select
              id="sched-facility"
              value={facility?.id ?? ''}
              onChange={(e) => {
                const f = facilities.find((x) => x.id === e.target.value) ?? null;
                setFacility(f);
              }}
              className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            >
              {facilities.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Week navigation */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={prevWeek}
            aria-label="Previous week"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground press"
          >
            <ChevronLeft className="size-4" strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={goToday}
            className="rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-foreground press"
          >
            Today
          </button>
          <button
            type="button"
            onClick={nextWeek}
            aria-label="Next week"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground press"
          >
            <ChevronRight className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        <div className="flex items-center gap-1.5 text-sm font-medium text-foreground num">
          <CalendarDays className="size-4 text-muted-foreground" strokeWidth={1.75} />
          {weekLabel}
        </div>
      </div>

      {/* Week grid */}
      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-lg bg-secondary"
              style={{ opacity: 1 - i * 0.2 }}
            />
          ))}
        </div>
      ) : loadError ? (
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <p className="text-sm text-destructive">{loadError}</p>
          <button
            type="button"
            onClick={() => void refresh()}
            className="mt-3 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Retry
          </button>
        </div>
      ) : !facility ? (
        <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
          <Wind className="mx-auto mb-3 size-8 text-muted-foreground/40" strokeWidth={1.25} />
          <p className="text-sm text-muted-foreground">No facility selected.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
          <WeekGrid
            weekStart={weekStart}
            shifts={shifts}
            facility={facility}
            memberLabel={memberLabel}
            onAddShift={(date) => setModal({ kind: 'editor', shift: null, defaultDate: date })}
            onEditShift={(shift) => setModal({ kind: 'editor', shift })}
            onDeleteShift={(shift) => setModal({ kind: 'delete', shift })}
          />
        </div>
      )}

      {/* Shift count summary */}
      {!loading && shifts.length > 0 && (
        <p className="text-xs text-muted-foreground num">
          {shifts.length} shift{shifts.length !== 1 ? 's' : ''} this week
          {shifts.filter((s) => s.status === 'cancelled').length > 0 && (
            <> · {shifts.filter((s) => s.status === 'cancelled').length} cancelled</>
          )}
        </p>
      )}

      {/* Modals */}
      {modal.kind === 'editor' && facility && (
        <ShiftEditor
          shift={modal.shift}
          defaultDate={modal.defaultDate}
          facility={facility}
          memberships={memberOptions}
          onClose={() => setModal({ kind: 'none' })}
          onSaved={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}
      {modal.kind === 'delete' && facility && (
        <DeleteShiftModal
          shift={modal.shift}
          tz={facility.timezone ?? 'UTC'}
          onCancel={() => setModal({ kind: 'none' })}
          onConfirmed={() => {
            setModal({ kind: 'none' });
            void refresh();
          }}
        />
      )}
    </div>
  );
}
